








































































































































import {Component, Prop, Vue, Watch} from "vue-property-decorator";
import TDSSpinner from "@/components/common/TDSSpinner.vue";
import FaqAccordion from "@/components/partials/FaqAccordion.vue";
import ActivePackageCard from "@/components/partials/CustomerProfile/ActivePackageCard.vue";
import PackageQueueCard from "@/components/partials/CustomerProfile/PackageQueueCard.vue";
import InstallPromptCard from "@/components/partials/CustomerProfile/InstallPromptCard.vue";
import LoadErrorCard from "@/components/partials/CustomerProfile/LoadErrorCard.vue";
import {CustomerProfile, CustomerBalance, ResellerInformation} from "@/interfaces/entities/CustomerProfile";
import {EsimProfile} from "@/interfaces/entities/EsimProfile";
import {resolveDeviceContext, SUPPORTED_LANGS} from "@/util/deviceContext";
import {getManualLocale, setManualLocale} from "@/util/localeOverride";
import {
    isDataExhaustedBalance,
    isValidityExpiredBalance,
    selectActiveBalance,
    selectQueuedBalances
} from "@/util/balancePlanStatus";
import {DemoScenario} from "@/demo/whitelabelDemoData";
import {isEsimAwaitingInstall, isEsimInstalled} from "@/util/esimState";
import {isValidShareToken} from "@/util/shareToken";

const DEFAULT_ACCENT_COLOR = "#0B7A73";
const REFRESH_MIN_SPIN_DURATION_MS = 800; // matches the `spin 0.8s` CSS animation
const LANGUAGE_LABELS: Record<string, string> = {en: "English", de: "Deutsch", ru: "Русский", es: "Español"};

@Component({
    components: {
        TDSSpinner,
        FaqAccordion,
        ActivePackageCard,
        PackageQueueCard,
        InstallPromptCard,
        LoadErrorCard
    }
})
export default class CustomerProfilePage extends Vue {
    // Set by the white-label demo page (WhitelabelDemo.vue), which renders this exact component
    // against fixture data with the branding the viewer is editing. Empty for the real route.
    @Prop({type: Boolean, default: false}) demo!: boolean;
    @Prop({default: "healthy"}) demoScenario!: DemoScenario;
    @Prop({default: null}) brandingOverride!: ResellerInformation | null;

    private profileDetails: CustomerProfile | null = null;
    private resellerInformation: ResellerInformation | null = null;
    private showInfo: boolean = false;
    private loadError: boolean = false;
    private refreshing: boolean = false;
    private retrying: boolean = false;
    private langMenuOpen: boolean = false;

    // -- Branding --

    // An explicit override wins over whatever the API returned, so the demo page can restyle
    // the live component without going anywhere near the fetch path.
    get branding(): ResellerInformation | null {
        return this.brandingOverride ?? this.resellerInformation;
    }

    get accentColor(): string {
        return this.branding?.accentColor || DEFAULT_ACCENT_COLOR;
    }

    get resellerName(): string {
        return this.branding?.name ?? "";
    }

    get resellerLogoUrl(): string {
        return this.branding?.logoUrl || "";
    }

    get faqUrl(): string {
        return this.branding?.faqUrl || "";
    }

    // -- eSIM & balance resolution --

    // The endpoint already scopes relatedEsims to the token's own profile domain, so no further
    // matching is needed here - only a preference for which one to show.
    get activeEsim(): EsimProfile | undefined {
        const active = (this.profileDetails?.relatedEsims ?? []).filter((eSim: EsimProfile) => eSim.active);
        // One that is on a device, else one still waiting to be installed, else whatever is left
        // (ERROR, DELETED, ...) - a broken eSIM is still worth naming rather than pretending to none.
        return active.find((eSim: EsimProfile) => isEsimInstalled(eSim.state))
            ?? active.find((eSim: EsimProfile) => isEsimAwaitingInstall(eSim.state))
            ?? active[0];
    }

    get isEsimInstalled(): boolean {
        return isEsimInstalled(this.activeEsim?.state);
    }

    // Only an eSIM that is genuinely waiting to be installed should prompt for installation.
    // An unusable one has nothing to offer, and neither does a profile with no eSIM at all.
    get isEsimAwaitingInstall(): boolean {
        return isEsimAwaitingInstall(this.activeEsim?.state);
    }

    // Deliberately independent of activeEsim. The endpoint returns only balances belonging to the
    // token's profile domain, so re-deriving them from the eSIM added nothing and cost everything:
    // a profile whose eSIM list was empty, or whose eSIM was in a state this page did not
    // recognise, showed "no data plan" while holding a perfectly good one.
    private get domainBalances(): Array<CustomerBalance> {
        return this.profileDetails?.balances ?? [];
    }

    get activeBalance(): CustomerBalance | undefined {
        return selectActiveBalance(this.domainBalances);
    }

    get queuedBalances(): Array<CustomerBalance> {
        return selectQueuedBalances(this.domainBalances, this.activeBalance);
    }

    // -- Lifecycle status (pill / status card / prompts) --
    // Only the expired/exhausted axis is needed here, to pick which prompt/pill
    // to show; see util/balancePlanStatus for the shared rules ActivePackageCard
    // also relies on for its usage panel and severity colors.

    private get isDataExhausted(): boolean {
        return isDataExhaustedBalance(this.activeBalance);
    }

    private get isValidityExpired(): boolean {
        return isValidityExpiredBalance(this.activeBalance);
    }

    get planStatus(): "expired" | "active" | "pending" {
        if (this.isValidityExpired) return "expired";
        // "pending" says there is something to install. Without an eSIM awaiting installation the
        // plan itself is simply active - reading "Ready to install" would send the customer after
        // an eSIM that is not there.
        return this.isEsimAwaitingInstall ? "pending" : "active";
    }

    // Layers "out of data" onto planStatus for display only - "expired" still wins.
    get displayStatus(): "expired" | "noData" | "active" | "pending" {
        if (this.planStatus === "expired") return "expired";
        if (this.planStatus === "active" && this.isDataExhausted) return "noData";
        return this.planStatus;
    }

    get planStatusLabel(): string {
        if (this.displayStatus === "expired") return this.$t("esimDataPlan.expired") as string;
        if (this.displayStatus === "noData") return this.$t("esimDataPlan.noDataRemaining") as string;
        if (this.displayStatus === "active") return this.$t("esimDataPlan.active") as string;
        return this.$t("esimDataPlan.readyToInstall") as string;
    }

    get planStatusDescription(): string {
        if (this.displayStatus === "expired") return this.$t("esimDataPlan.statusExpiredDescription") as string;
        if (this.displayStatus === "noData") return this.$t("esimDataPlan.statusNoDataDescription") as string;
        if (this.displayStatus === "active") return this.$t("esimDataPlan.statusActiveDescription") as string;
        return this.$t("esimDataPlan.statusReadyDescription") as string;
    }

    // -- Locale & FAQ --

    get currentLocale(): string {
        return this.$i18n.locale;
    }

    get languageOptions(): Array<{code: string; label: string}> {
        return SUPPORTED_LANGS.map((code: string) => ({code, label: LANGUAGE_LABELS[code] ?? code.toUpperCase()}));
    }

    get faqItems(): Array<{question: string; answer: string}> {
        return this.$t("esimDataPlan.faq.questions") as unknown as Array<{question: string; answer: string}>;
    }

    created() {
        if (!getManualLocale()) {
            this.$i18n.locale = resolveDeviceContext().lang;
        }
        this.fetchCustomerData();
    }

    mounted() {
        document.addEventListener("click", this.handleOutsideLangClick);
    }

    beforeDestroy() {
        document.removeEventListener("click", this.handleOutsideLangClick);
    }

    // -- User actions --

    private async shareUrl() {
        const url = window.location.href;
        if (navigator.share) {
            try {
                await navigator.share({title: this.$t("esimDataPlan.title") as string, url});
            } catch (e) {
                // User cancelled the native share sheet - not an error.
            }
        } else {
            await navigator.clipboard.writeText(url);
            this.$toastr.success(this.$t("esimDataPlan.linkCopied") as string);
        }
    }

    private toggleLangMenu() {
        this.langMenuOpen = !this.langMenuOpen;
    }

    private selectLocale(code: string) {
        this.$i18n.locale = code;
        setManualLocale(code);
        this.langMenuOpen = false;
    }

    private handleOutsideLangClick(event: MouseEvent) {
        const wrapper = this.$refs.langDropdown as HTMLElement | undefined;
        if (this.langMenuOpen && wrapper && !wrapper.contains(event.target as Node)) {
            this.langMenuOpen = false;
        }
    }

    private getTokenFromInstallationUrl(url: string): string | null {
        try {
            const match = url?.match(/token=([^&]+)/);
            return match ? match[1] : null;
        } catch {
            return null;
        }
    }

    private navigateToEsimInstall() {
        if (this.demo) {
            // No real token to hand over - let the demo page swap its preview instead.
            this.$emit("navigate-install");
            return;
        }
        const token = this.getTokenFromInstallationUrl(this.activeEsim?.installationUrl ?? "");
        if (token) {
            this.$router.push({path: "/esim/profile", query: {token}});
        }
    }

    private async refresh() {
        if (this.refreshing) return;
        this.refreshing = true;
        // Guarantee a full spin so the click visibly registers even on instant (mock) responses.
        await this.delay(REFRESH_MIN_SPIN_DURATION_MS);
        await this.fetchCustomerData();
        this.refreshing = false;
    }

    private async retryFetch() {
        if (this.retrying) return;
        this.retrying = true;
        // Guarantee a full spin so the click visibly registers even on instant (mock) responses.
        await this.delay(REFRESH_MIN_SPIN_DURATION_MS);
        await this.fetchCustomerData();
        this.retrying = false;
    }

    private delay(ms: number): Promise<void> {
        return new Promise((resolve: () => void) => setTimeout(resolve, ms));
    }

    // -- Data fetching & auth --

    @Watch("demoScenario")
    private onDemoScenarioChanged() {
        if (this.demo) this.fetchCustomerData();
    }

    private async fetchCustomerData() {
        if (this.demo) {
            // Loaded on demand, so the demo fixtures stay out of this page's own module graph.
            const {demoCustomerProfile} = await import(/* webpackChunkName: "whitelabel-demo-data" */ "@/demo/whitelabelDemoData");
            this.profileDetails = demoCustomerProfile(this.demoScenario);
            this.resellerInformation = null;
            this.showInfo = true;
            this.loadError = false;
            return;
        }

        const token = this.$route.query.token ?? "";
        const mockMode = process.env.VUE_APP_MOCK_MODE === "true";
        if (!mockMode && !isValidShareToken(token)) {
            this.$toastr.error(this.$t("esimInstallation.error.tokenInvalid")).then(() => location.reload());
        } else {
            try {
                this.profileDetails = await this.$store.dispatch("FETCH_CUSTOMER_DATA", {token, includeResellerInfo: true});
                this.resellerInformation = this.profileDetails?.resellerInformation ?? null;
                this.showInfo = true;
                this.loadError = false;
            } catch (e) {
                this.loadError = true;
            }
        }
    }
}
